import React from 'react';
import { connect } from 'react-redux';

import withLocalize, { IWithLocalizeOwnProps } from '@src/components/common/localize/withLocalize';
import UserView from '@src/components/user/view/UserView';
import { IFile } from '@src/model/file/File';
import { IUserInfo } from '@src/model/user/User';
import { IWorkPosition } from '@src/model/user/WorkPosition';
import CollectionBusinessStore from '@src/service/business/common/collectionBusinessStore';
import { IUserFeedbackMessagePayload, UserFeedbackMessageSeverity, UserFeedbackMessageType } from '@src/service/business/common/types';
import UserFeedbackBusinessStore from '@src/service/business/common/userFeedbackBusinessProvider';
import LoginBusinessStore from '@src/service/business/login/loginBusinessStore';
import { UserBusinessStore } from '@src/service/business/user/UserBusinessStore';
import { createActionThunk, IActionThunkMap } from '@src/service/util/action/thunk';
import { createTrackableAction, ITrackableAction } from '@src/service/util/action/trackAction';
import { withRouter, WithRouterProps } from 'react-router';

// -- Const
// ----------

// --
// ----- Prop types
interface IUserViewContainerOwnProps {
  userId: string;
  openedTab: string;
  baseRoute: string;
}

interface IUserViewContainerStateProps {
  currentUser: IUserInfo;
  user: IUserInfo;
  workPositionList: IWorkPosition[];
}

interface IUserViewContainerDispatchProps {
  fetchUser: (id: string) => void;
  updateUser: (data: IUserInfo) => ITrackableAction;
  clearUser: () => void;
  createUserAvatar: (id: string, data: IFile, thunkMap: IActionThunkMap) => void;
  deleteUser: (id: string) => ITrackableAction;
  reportMessage: (data: IUserFeedbackMessagePayload) => void;
  updateNotz: (id: string) => ITrackableAction;
}

type IUserViewContainerProps = IUserViewContainerOwnProps & IUserViewContainerStateProps & IUserViewContainerDispatchProps & WithRouterProps & IWithLocalizeOwnProps;
// --
// ----- State types
interface IUserViewContainerState {
  isEditingUser: boolean;
}

// --
// ----- Component
class UserViewContainer extends React.Component<IUserViewContainerProps, IUserViewContainerState> {
  state: IUserViewContainerState = { isEditingUser: false };

  componentDidMount = () => {
    // initial data load
    this.fetchUser();
  };

  componentWillUnmount = () => {
    this.props.clearUser();
  };

  render = () => {
    return (
      <React.Fragment>
        {this.props.user && (
          <UserView
            user={this.props.user}
            currentUser={this.props.currentUser}
            openedTab={this.props.openedTab}
            workPositionList={this.props.workPositionList}
            isEditingUser={this.state.isEditingUser}
            onAvatarSubmit={this.onAvatarSubmit}
            onUserSubmit={this.handleFormSubmit}
            onUserUpdate={this.fetchUser}
            onUserDelete={this.handleUserDelete}
            onTabChange={this.handleTabChange}
            onNotzUpdate={this.handleNotzUpdate}
            setUserEdit={this.setUserEdit}
          />
        )}
      </React.Fragment>
    );
  };

  handleNotzUpdate = () => {
    const id = this.props.userId ? this.props.userId : this.props.currentUser.id;
    this.props
      .updateNotz(id)
      .track()
      .subscribe(
        // success
        () => this.fetchUser()
      );
  };

  handleTabChange = (key: string) => {
    this.props.router.push(`/${this.props.baseRoute}/${key}`);
  };

  private fetchUser = () => {
    const id = this.props.userId ? this.props.userId : this.props.currentUser.id;
    this.props.fetchUser(id);
  };

  private onAvatarSubmit = (data: IFile) => {
    this.props.createUserAvatar(this.props.user.id, data, {
      success: () => {
        this.fetchUser();
      },
    });
  };

  private handleFormSubmit = (data: IUserInfo) => {
    this.props
      .updateUser(data)
      .track()
      .subscribe(() => {
        this.props.reportMessage({ message: this.props.translate('GENERAL_MESSAGE.GENERAL_UPDATE_SUCCESS'), type: UserFeedbackMessageType.NOTIFICATION, severity: UserFeedbackMessageSeverity.SUCCESS });
        this.setUserEdit(false);
      });
  };

  private handleUserDelete = (data: IUserInfo) => {
    this.props
      .deleteUser(data.id)
      .track()
      .subscribe(() => this.props.router.push('/users'));
  };

  private setUserEdit = (visible: boolean) => {
    this.setState({ isEditingUser: visible });
  };
}

// `state` parameter needs a type annotation to type-check the correct shape of a state object but also it'll be used by "type inference" to infer the type of returned props
const mapStateToProps = (state: any, ownProps: IUserViewContainerOwnProps): IUserViewContainerStateProps => ({
  currentUser: LoginBusinessStore.selectors.getCurrentUser(state),
  user: UserBusinessStore.selectors.getUser(state),
  workPositionList: CollectionBusinessStore.selectors.getCollectionContent(state, 'WorkPosition'),
});

// `dispatch` parameter needs a type annotation to type-check the correct shape of an action object when using dispatch function
const mapDispatchToProps = (dispatch: any, ownProps: IUserViewContainerOwnProps): IUserViewContainerDispatchProps => ({
  fetchUser: (id: string) => dispatch(UserBusinessStore.actions.fetchUser({ id })),
  updateUser: (data: IUserInfo) => dispatch(createTrackableAction(UserBusinessStore.actions.updateUser(data))),
  clearUser: () => dispatch(UserBusinessStore.actions.clearUser()),
  createUserAvatar: (id: string, data: IFile, thunkMap: IActionThunkMap) => dispatch(createActionThunk(UserBusinessStore.actions.createUserAvatar(id, data), thunkMap)),
  deleteUser: (id: string) => createTrackableAction(dispatch(UserBusinessStore.actions.deleteUser({ id }))),
  reportMessage: (data: IUserFeedbackMessagePayload) => dispatch(UserFeedbackBusinessStore.actions.reportMessage(data)),
  updateNotz: (userId: string) => createTrackableAction(dispatch(UserBusinessStore.actions.updateUserNotz(userId))),
});

export default connect<IUserViewContainerStateProps, IUserViewContainerDispatchProps, IUserViewContainerOwnProps>(mapStateToProps, mapDispatchToProps)(withLocalize(withRouter<IUserViewContainerOwnProps>(UserViewContainer as any)));
